import { CtaType } from "@/components/molecules/CtaButtonGroup";
import classNames from "classnames";
import SectionHeader from "./shared/SectionHeader";

/**
 * ColoredOutcomeCardsSection — centered title + subtitle + CTA pair,
 * followed by 4 large colored gradient cards. Each card has a title at
 * top-left and a row of tag pills below. Used by feature V2 pages for
 * the "Engagement. Retention. Revenue." outcome block.
 */

export type ColoredOutcomeCardAccent = "blue" | "green" | "yellow" | "pink";

export type ColoredOutcomeCard = {
  title: string;
  tags: string[];
  accent: ColoredOutcomeCardAccent;
};

export type ColoredOutcomeCardsSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  cards: ColoredOutcomeCard[];
};

const accentGradient: Record<ColoredOutcomeCardAccent, string> = {
  blue: "bg-gradient-to-br from-[#E8F1FF] to-[#9DC3FF]",
  green: "bg-gradient-to-br from-[#E7FAE3] to-[#9DE88A]",
  yellow: "bg-gradient-to-br from-[#FFF6D9] to-[#FFD06A]",
  pink: "bg-gradient-to-br from-[#FFE6F2] to-[#FFAAD0]",
};

const Card = ({ card }: { card: ColoredOutcomeCard }) => (
  <div
    className={classNames(
      "flex flex-col rounded-3xl p-5 md:min-h-[320px] md:p-8",
      accentGradient[card.accent]
    )}
  >
    <h3 className="font-inter text-[18px] font-semibold leading-snug text-neutralBlack md:text-[20px]">
      {card.title}
    </h3>
    <div className="mt-4 flex flex-wrap gap-2">
      {card.tags.map((t) => (
        <span
          key={t}
          className="rounded-full bg-white px-3 py-1.5 font-inter text-[13px] font-medium text-neutralBlack"
        >
          {t}
        </span>
      ))}
    </div>
  </div>
);

const ColoredOutcomeCardsSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  cards,
}: ColoredOutcomeCardsSectionData) => (
  <section className="relative w-full bg-white">
    <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
      <SectionHeader
        eyebrow={eyebrow}
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        subtitle={subtitle}
        ctas={ctas}
        variant="light-teal"
        align="center"
      />
      <div className="mx-auto mt-12 grid max-w-6xl gap-5 md:mt-16 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.title} card={c} />
        ))}
      </div>
    </div>
  </section>
);

export default ColoredOutcomeCardsSection;
